import { useEffect, useState } from "react";

// DEPENDENCIES
import * as Sentry from "@sentry/react";

// TYPES
import { ComponentProps, Entity } from "../../types";

// UTILS
import { genericToastifyError, scrollToElement } from "../../utils";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import {
  onUpdateActiveIndex,
  onUpdateIsFormOpen,
} from "../../reducers/collectionReducer";
import {
  onUpdatePrimaryButtonModal,
  openModal,
} from "../../reducers/modalReducer";

// HOOKS
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useWindowWidth from "../../hooks/useWindowWidth";
import useForm from "../../hooks/useForm";
import useAlertFeedback from "../../hooks/useAlertFeedback";

// COMPONENTS
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { Alert, AlertProps } from "@allaria-tech/matter/Alert";
import { Feedback } from "@allaria-tech/matter/Feedback";
import { Icon } from "@allaria-tech/matter/Icon";
import { Typography } from "@allaria-tech/matter/Typography";
import EntityCard from "./EntityCard";

import styles from "./EntitySelector.module.scss";

interface AlertContent {
  title: string;
  message?: string;
}

interface EntitySelectorProps extends ComponentProps {
  entityType: string;
  title?: string;
  unsaveChangesAlert: AlertContent;
  infoAlert?: AlertProps;
  entities: Entity[];
  handleEntities: () => void;
  isCompletedEntity: (i: number) => boolean;
  addEntityButton?: {
    text: string;
    mobileText?: string;
    onClick: () => void;
    disabled?: boolean;
    hide?: boolean;
  };
  onContinueButton: {
    onClick: () => Promise<void>;
    disabled?: boolean;
  };
  disabledFirst?: boolean;
}

const EntitySelector = ({
  formik,
  entityType,
  unsaveChangesAlert,
  title,
  infoAlert,
  entities,
  handleEntities,
  addEntityButton,
  onContinueButton,
  isCompletedEntity,
  disabledFirst,
}: EntitySelectorProps) => {
  const { container, content, entitiesContainer, addEntityRow, notClickable } =
    styles;

  const [buttonIsLoading, setButtonIsLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { isEditing, activeIndex, isFormOpen } = useAppSelector(
    (state) => state.collectionData,
  );
  const formValues = useAppSelector((state) => state.onboardingData.formValues);

  const { handleBack } = useForm(formik);
  const discardChangesAlert = useAlertFeedback("discard-new-changes");

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const isActive = (index: number) => activeIndex === index;
  const showDiscardNewChangesAlert = (i: number) => isEditing && !isActive(i);

  // Mobile shows one at a time: opening an entity hands the screen over to its
  // form, which comes back through the form's Page Header
  const openEntity = (index: number) => {
    dispatch(onUpdateActiveIndex(index));
    if (isMobile) dispatch(onUpdateIsFormOpen(true));
  };

  const handleAddEntity = () => {
    addEntityButton?.onClick();
    scrollToElement(`${entityType}-${entities.length}`);
    openEntity(entities?.length + 1);
  };

  const goToEntity = (i: number) => {
    if (i === 0 && disabledFirst) return;

    if (showDiscardNewChangesAlert(i + 1)) {
      dispatch(openModal({ name: "discard-new-changes" }));
      dispatch(
        onUpdatePrimaryButtonModal({
          text: "Sí, eliminar",
          action: () => openEntity(i + 1),
        }),
      );
      return;
    }

    return openEntity(i + 1);
  };

  // Mobile hands the row over to its own edit button, so the card itself stops
  // being the affordance
  const handleClickEntity = (i: number) => {
    if (isMobile) return;

    return goToEntity(i);
  };

  const isDisableOnContinue =
    entities.length === 0 || onContinueButton.disabled;

  const onClickOnContinue = async () => {
    try {
      setButtonIsLoading(true);
      await onContinueButton.onClick();
    } catch (error) {
      Sentry.captureException(error);
      showToastMessage(genericToastifyError, {
        containerId: "layoutPage",
        type: "ERROR",
      });
      throw new Error(`Error at ${entityType} : ${error}`);
    } finally {
      setButtonIsLoading(false);
    }
  };

  useEffect(() => {
    handleEntities();
  }, [entities.length]);

  // The step opens on the list, so a resumed onboarding never lands on a form
  // whose card it has no way back to
  useEffect(() => {
    dispatch(onUpdateIsFormOpen(false));

    if (disabledFirst && activeIndex === 1) dispatch(onUpdateActiveIndex(2));
  }, []);

  // Mobile has no add row of its own, so the add action takes the footer's
  // primary slot and pushes the step's own actions down one: Continuar becomes
  // secondary and Volver tertiary
  const addEntityInFooter =
    isMobile && !!addEntityButton && !addEntityButton.hide;

  // While the form owns the screen it owns the footer too, saving and going
  // back to the list instead of continuing the step
  useEffect(() => {
    if (isFormOpen) return;

    dispatch(
      onUpdateSecondaryButton(
        addEntityInFooter
          ? {
              text: "Continuar",
              disabled: isDisableOnContinue,
              isLoading: buttonIsLoading,
              onClick: onClickOnContinue,
              show: true,
            }
          : {
              text: "Volver",
              disabled: false,
              isLoading: false,
              onClick: handleBack,
              show: true,
            },
      ),
    );
  }, [addEntityInFooter, isDisableOnContinue, buttonIsLoading, isFormOpen]);

  useEffect(() => {
    if (isFormOpen) return;

    dispatch(
      onUpdatePrimaryButton(
        addEntityInFooter
          ? {
              text: addEntityButton.mobileText || addEntityButton.text,
              disabled: addEntityButton.disabled,
              isLoading: false,
              onClick: handleAddEntity,
              show: true,
            }
          : {
              text: "Continuar",
              disabled: isDisableOnContinue,
              isLoading: buttonIsLoading,
              onClick: onClickOnContinue,
              show: true,
            },
      ),
    );
  }, [
    addEntityInFooter,
    addEntityButton?.disabled,
    isDisableOnContinue,
    buttonIsLoading,
    formValues,
    isFormOpen,
  ]);

  useEffect(() => {
    if (isFormOpen) return;

    dispatch(
      onUpdateTertiaryButton({
        text: "Volver",
        disabled: false,
        isLoading: false,
        onClick: handleBack,
        show: addEntityInFooter,
      }),
    );
  }, [addEntityInFooter, isFormOpen]);

  // The step's own button reset only covers field steps, so the third slot this
  // one opened has to be closed on the way out or it follows into the next step
  useEffect(
    () => () => {
      dispatch(onUpdateTertiaryButton({ show: false }));
    },
    [],
  );

  if (isMobile && isFormOpen) return null;

  return (
    <section className={container}>
      <Feedback
        {...discardChangesAlert}
        type="WARNING"
        title={unsaveChangesAlert.title}
        description={
          unsaveChangesAlert.message ||
          "Se descartarán los datos ingresados que no fueron guardados."
        }
      />
      {title && (
        <Typography type="HEADING" size="S" weight="EMPHASIS">
          {title}
        </Typography>
      )}
      <div className={`${content} scrollbar`}>
        {infoAlert && <Alert hasSummary {...infoAlert} />}
        <div className={entitiesContainer}>
          {entities?.map((entity, i) => (
            <EntityCard
              key={`${entityType}-${i}`}
              id={`${entityType}-${i}`}
              entity={entity}
              isActive={isActive(i + 1)}
              disabled={i === 0 && disabledFirst}
              onClick={() => handleClickEntity(i)}
              onEdit={() => goToEntity(i)}
              isCompletedEntity={isCompletedEntity(i)}
              showEditAction={isMobile}
            />
          ))}
          {!isMobile && addEntityButton && !addEntityButton.hide && (
            <div
              className={`${addEntityRow} ${
                addEntityButton.disabled ? notClickable : ""
              }`}
              onClick={addEntityButton.disabled ? undefined : handleAddEntity}
            >
              <Typography
                type="LABEL"
                size="S"
                color="var(--typography-label-color-disabled, #8690a1)"
              >
                {addEntityButton.text}
              </Typography>
              <Icon
                name="chevron-right"
                size="sm"
                color="var(--typography-label-color-disabled, #8690a1)"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EntitySelector;
